import { useEffect, useRef } from 'react';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@shared/services/firebase';
import { saveDailyMoneyMood } from '@shared/services/userContentService';
import { MoneyMood } from '@shared/types';
import { useAuth } from '../../context/AuthContext';
import { logError } from '@/lib/log';

const ONBOARDING_RESPONSES_KEY = 'enough_onboarding_responses';

interface OnboardingResponses {
    moneyMood?: string;
    debtRange?: string;
    transactionReflection?: 'needed' | 'wanted' | null;
}

/**
 * Syncs onboarding responses from localStorage to Firestore once after signup.
 * Runs once per mount when user is authenticated and pending data exists.
 */
export function useOnboardingSync() {
    const { currentUser } = useAuth();
    const hasSynced = useRef(false);

    useEffect(() => {
        if (!currentUser?.uid || hasSynced.current) return;

        const raw = localStorage.getItem(ONBOARDING_RESPONSES_KEY);
        if (!raw) return;

        let responses: OnboardingResponses;
        try {
            responses = JSON.parse(raw);
        } catch {
            localStorage.removeItem(ONBOARDING_RESPONSES_KEY);
            return;
        }

        hasSynced.current = true;

        const sync = async () => {
            const uid = currentUser.uid;

            // Save money mood via existing service
            if (responses.moneyMood) {
                await saveDailyMoneyMood(uid, responses.moneyMood as MoneyMood);
            }

            // Save debt range to user content root doc
            if (responses.debtRange) {
                const userContentRef = doc(db, 'userContent', uid);
                await setDoc(userContentRef, {
                    onboardingDebtRange: responses.debtRange,
                    updatedAt: serverTimestamp()
                }, { merge: true });
            }

            // transactionReflection is a demo interaction — not persisted

            localStorage.removeItem(ONBOARDING_RESPONSES_KEY);
        };

        sync().catch((err) => {
            logError(err, { context: 'sync_onboarding_responses' });
            // Reset so it can retry next mount
            hasSynced.current = false;
        });
    }, [currentUser]);
}
